import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './LoginPage.css';
import './ResetPasswordPage.css';
import logo from '../../assets/logo.png';
import hipaa from '../../assets/VitaRPM-HIPAA.png';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    password_confirmation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setLocalError('Invalid reset link. Please request a new password reset.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (localError) {
      setLocalError('');
    }
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      return 'Password must contain uppercase, lowercase, numbers, and special characters.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setLocalError('');
    setSuccessMessage('');

    if (!token) {
      setLocalError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!formData.password || formData.password.trim() === '') {
      setLocalError('Password is required.');
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      setLocalError('Passwords do not match.');
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setLocalError(passwordError);
      return;
    }

    setShowLoading(true);

    try {
      const response = await fetch(`${API_BASE}/users/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setLocalError(data.message || `Reset failed (HTTP ${response.status})`);
        return;
      }

      if (!data.success) {
        setLocalError(data.message || 'Password reset failed');
        return;
      }

      setSuccessMessage(data.message || 'Password has been reset successfully.');

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      setLocalError('An error occurred. Please try again.');
    } finally {
      setShowLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowPasswordConfirmation(!showPasswordConfirmation);
    }
  };

  return (
    <div className="vrpm-login-page">
      {/* LEFT PANEL */}
      <div className="vrpm-login-left">

        <div className="vrpm-logo">
          <img src={logo} alt="VitaRPM" />
        </div>

        <div className="vrpm-form-wrapper">

          <div
            className="vrpm-back-link"
            onClick={() => navigate('/forgot-password')}
            style={{ cursor: 'pointer' }}
          >
            <i className="fa fa-arrow-left"></i>
            <span>Back to Forgot Password</span>
          </div>

          <h1>Reset Password</h1>

          <p className="vrpm-subtitle">
            Enter your new password below. It must be at least 8 characters and contain uppercase, lowercase, numbers, and special characters.
          </p>

          <form onSubmit={handleSubmit} noValidate autoComplete="off">

            <div className="vrpm-field">
              <label htmlFor="password">New Password</label>
              <div className="vrpm-password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  required
                  autoComplete="new-password"
                  autoFocus
                />
                <i
                  className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                  onClick={() => togglePasswordVisibility('password')}
                  aria-hidden="true"
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>

            <div className="vrpm-field">
              <label htmlFor="password_confirmation">Confirm Password</label>
              <div className="vrpm-password-wrapper">
                <input
                  id="password_confirmation"
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                />
                <i
                  className={`fa ${showPasswordConfirmation ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                  onClick={() => togglePasswordVisibility('password_confirmation')}
                  aria-hidden="true"
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>

            {successMessage && (
              <div className="alert-success-custom mb-4" role="alert">
                {successMessage}
              </div>
            )}

            {localError && (
              <div className="vrpm-error">
                {localError}
              </div>
            )}

            <button
              type="submit"
              className="vrpm-login-btn"
              disabled={showLoading || !token}
            >
              {showLoading ? 'Resetting...' : 'Reset Password'}
            </button>

          </form>

        </div>

      </div>

      {/* RIGHT PANEL */}
      <div className="vrpm-login-right-container">
        <div className="vrpm-login-right">
          <div className="vrpm-right-overlay">

            <div className="vrpm-hero">
              <h2>
                Revolutionize Patient Care <br></br>with VitaRPM
              </h2>

              <p className="vrpm-quote">
                “With VitaRPM, our clinicians stay connected to patients beyond the clinic.
                The platform is intuitive, secure, and provides the visibility we need to intervene early
                and improve patient outcomes.”
              </p>

              <div className="vrpm-testimonial">
                <div className="vrpm-avatar">
                  <img
                    src="https://randomuser.me/api/portraits/women/71.jpg"
                    alt=""
                  />
                </div>
                <div>
                  <h4>Isabela Torres, MD</h4>
                  <span>Director Digital Health</span>
                </div>
              </div>
            </div>

            <div className="vrpm-bottom">
              <div className="vrpm-divider"></div>
              <div className="vrpm-hipaa">
                <img src={hipaa} alt="VitaRPM" />
              </div>
              <div className="vrpm-cpt-grid">
                <div>CPT 99453</div>
                <div>CPT 99445</div>
                <div>CPT 99454</div>
                <div>CPT 99470</div>
                <div>CPT 99457</div>
                <div>CPT 99458</div>
              </div>
            </div>

          </div>
        </div>
      </div>

    </div>
  );
};

export default ResetPasswordPage;
